'use client';

import { cn } from '@core/utils';
import { chunk } from 'es-toolkit';
import { useState } from 'react';

import { CustomLink } from '@/app/_components/CustomLink';
import { NewBadge } from '@/app/donation/_components/NewBadge';
import { HorizontalScroll, Image } from '@/components/client';
import { DONATION_ROUTES } from '@/constants';

import type { getInstitutionSpecialties } from '../../_utils/getInstitutionSpecialties';

interface SpecialtiesItemProps {
  imageUrl: string;
  specialtyName: string;
  institutionName: string;
  href: string;
  isNew?: boolean;
  className?: string;
}

/** 모바일, 태블릿에서 사용되는 컴포넌트 */
const SpecialtiesItem = ({
  imageUrl,
  specialtyName,
  institutionName,
  href,
  isNew,
  className,
}: SpecialtiesItemProps) => {
  return (
    <CustomLink
      href={href}
      className={cn(
        'flex w-56 shrink-0 flex-col items-center gap-4 rounded-medium font-600 text-Gray-600 transition-opacity duration-200 ease-out typo-caption1',
        'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-B-500',
        'active:opacity-80',
        'xl:hover:opacity-80',
        className,
      )}
    >
      <span className="relative mb-4 block size-48">
        <Image
          src={imageUrl}
          alt={specialtyName}
          width={48}
          height={48}
          className="size-48 object-contain"
          loading="lazy"
        />
        {isNew && (
          <div className="absolute bottom-[-6px] left-1/2 -translate-x-1/2">
            <NewBadge dimensions="H16" />
          </div>
        )}
      </span>
      <p className="line-clamp-1 text-center">{institutionName}</p>
    </CustomLink>
  );
};

interface RegionalSpecialtiesSectionProps {
  list: Awaited<ReturnType<typeof getInstitutionSpecialties>>;
  className?: string;
}

export const RegionalSpecialtiesSection = ({
  list,
  className,
}: RegionalSpecialtiesSectionProps) => {
  const [progressPercent, setProgressPercent] = useState(0);
  const [isScrollable, setIsScrollable] = useState(false);

  //* 셔플은 서버에서 1회 수행되어 list 순서 그대로 사용 (hydration 일관)
  const sortedItems = list.map((item) => ({
    imageUrl: item.specialtyImageUrl,
    specialtyName: item.specialtyName,
    institutionName: item.institutionShortName,
    href: DONATION_ROUTES.HOMETOWNS_DETAIL.pathname({
      id: item.slugId ?? item.institutionId,
    }),
    isNew: item.newDisplayYN,
  }));

  //* 두 줄로 표시할지 여부
  const isTwoRow = sortedItems.length > 10;

  //* 두 줄로 표시할지 여부에 따라 렌더링 할 아이템 목록 생성
  const renderItems = isTwoRow
    ? chunk(sortedItems, 2)
    : sortedItems.map((item) => [item]);

  const swiperScrollProgress = (() => {
    if (progressPercent >= SCROLL_PROGRESS_MAX_LEFT) {
      return SCROLL_PROGRESS_MAX_LEFT;
    }
    if (progressPercent <= SCROLL_PROGRESS_MIN_LEFT) {
      return SCROLL_PROGRESS_MIN_LEFT;
    }

    return progressPercent;
  })();

  return (
    <section
      className={cn(
        'flex w-full flex-col items-center gap-20 overflow-hidden pb-32',
        className,
      )}
    >
      <div className="flex w-full overflow-visible">
        <HorizontalScroll
          className="mx-auto px-20"
          slideClassName="gap-12"
          onProgress={(progress) => {
            setProgressPercent(progress * PROGRESS_MAX_PERCENT);
          }}
          onScrollableChange={setIsScrollable}
        >
          {renderItems.map((item, index) => (
            <article key={index} className="flex flex-col gap-14">
              {item.map((item, index) => (
                <SpecialtiesItem key={index} {...item} />
              ))}
            </article>
          ))}
        </HorizontalScroll>
      </div>

      {/* 콘텐츠가 다 들어오면(w-fit이 뷰포트를 콘텐츠 폭에 맞춰 데스크톱에선 흔하다) 스크롤이
          발생할 수 없다 — 인디케이터를 남겨두면 움직이지 않는 거짓 신호가 된다.
          자리는 유지해야 스켈레톤과 높이가 어긋나지 않으므로 숨기기만 한다. */}
      <div
        className={cn(
          'relative h-3 w-60 overflow-hidden rounded-pill bg-Gray-300',
          !isScrollable && 'invisible',
        )}
      >
        <div
          className="absolute left-0 top-0 h-full rounded-pill bg-Gray-600"
          style={{
            width: `${SCROLL_WIDTH}%`,
            left: `${swiperScrollProgress}%`,
          }}
        />
      </div>
    </section>
  );
};

const PROGRESS_MAX_PERCENT = 100;
const SCROLL_WIDTH = 20;
const SCROLL_PROGRESS_MAX_LEFT = PROGRESS_MAX_PERCENT - SCROLL_WIDTH;
const SCROLL_PROGRESS_MIN_LEFT = 0;
